import { CONSENT_CHANGED_EVENT } from '@/stores/cookieConsent';

/**
 * Formats cart items for Google Analytics (GA4)
 * @param {Array} cartItems - The raw cart items
 * @param {Object} services - The services structure from the store (optional, for group assignment)
 * @returns {Array} GA4-compatible items
 */
function getCartItemsForGA(cartItems, services = {}) {
    const groupMap = {};
    Object.entries(services).forEach(([key, service]) => {
        if (service && service.group && service.group.title) {
            service.items?.forEach(item => {
                groupMap[item.id] = service.group.title;
            });
        }
    });
    return (cartItems || []).map(i => ({
        item_id: i.id ? String(i.id) : undefined,
        item_name: i.name,
        item_category: i.date || '',
        item_category2: groupMap[i.id] || '',
        price: normalizeNumber(i.price),
        currency: currencyMapping(i.currency),
        quantity: normalizeNumber(i.quantity) || 1
    }));
}

/**
 * GA, UET and other tracking utilities for Magic Ticketing
 */

const GA_EVENTS = {
    VIEW_ITEM_LIST: "view_item_list",
    SELECT_ITEM: "select_item",
    VIEW_ITEM: "view_item",
    ADD_TO_CART: "add_to_cart",
    REMOVE_FROM_CART: "remove_from_cart",
    VIEW_CART: "view_cart",
    ADD_SHIPPING_INFO: "add_shipping_info",
    ADD_PAYMENT_INFO: "add_payment_info",
    BEGIN_CHECKOUT: "begin_checkout",
    PURCHASE: "purchase",
    REFUND: "refund",
    VIEW_PROMOTION: "view_promotion",
    SELECT_PROMOTION: "select_promotion",
    LOGIN: "login",
    SIGN_UP: "sign_up",
    SHARE: "share",
    SELECT_CONTENT: "select_content",
    SEARCH: "search"
};

const GA_METHODS = {
    MT: "MT"
};

const CURRENCY_MAP = {
    "€": "EUR",
    "EUR": "EUR",
    "$": "USD",
    "USD": "USD"
};

/**
 * Checks if tracking is enabled based on user consent.
 * @returns {boolean}
 */
function isTrackingEnabled(category = "analytics") {
    const localStorageConsent = getLocalStorageConsent();
    return localStorageConsent !== null && localStorageConsent[category] === true;
}

/**
 * Reads consent from the cookie consent store.
 * @returns {{analytics: boolean, marketing: boolean}|null}
 */
function getLocalStorageConsent() {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return null;
    }

    try {
        const raw = localStorage.getItem('mt-cookie-consent-v1');
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        const preferences = parsed?.preferences;
        if (!preferences || typeof preferences !== 'object') {
            return null;
        }

        return {
            analytics: preferences.analytics === true,
            marketing: preferences.marketing === true,
        };
    } catch (e) {
        return null;
    }
}

/**
 * Wraps a function with tracking checks.
 * @param {function} fn
 * @param {string} category
 * @returns {function}
 */
function withTrackingCheck(fn, category = "analytics") {
    return function(...args) {
        if (!isTrackingEnabled(category)) return;
        return fn.apply(this, args);
    }
}

/**
 * Parses different number formats into a finite number.
 * @param {string|number} value
 * @returns {number}
 */
function normalizeNumber(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }

    if (typeof value !== 'string') {
        return 0;
    }

    let normalized = value.trim().replace(/[\s']/g, '');
    if (!normalized) return 0;

    const hasComma = normalized.includes(',');
    const hasDot = normalized.includes('.');

    if (hasComma && hasDot) {
        const decimalSeparator = normalized.lastIndexOf(',') > normalized.lastIndexOf('.') ? ',' : '.';
        const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';

        normalized = normalized.split(thousandsSeparator).join('');
        if (decimalSeparator === ',') {
            normalized = normalized.replace(',', '.');
        }
    } else if (hasComma) {
        if (/^-?\d{1,3}(,\d{3})+$/.test(normalized)) {
            normalized = normalized.replace(/,/g, '');
        } else {
            normalized = normalized.replace(',', '.');
        }
    } else if (hasDot) {
        if (/^-?\d{1,3}(\.\d{3})+$/.test(normalized)) {
            normalized = normalized.replace(/\./g, '');
        }
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Maps currency symbols to their respective codes.
 * @param {string} value 
 * @returns {string}
 */
function currencyMapping(value) {
    if (typeof value !== "string") return "EUR";
    return CURRENCY_MAP[value.toUpperCase()] || CURRENCY_MAP[value] || "EUR";
}

/**
 * Checks if the provided items are valid for tracking.
 * @param {*} items 
 * @returns {boolean}
 */
function isValidItems(items) {
    return Array.isArray(items) && items.length > 0;
}

/**
 * Builds GA4 ecommerce params with optional properties.
 * @param {object} params
 * @returns {object}
 */
function buildEcommerceParams(params = {}) {
    const eventParams = {
        currency: params.currency !== undefined ? currencyMapping(params.currency) : undefined,
        value: params.value !== undefined ? normalizeNumber(params.value) : undefined,
        tax: params.tax !== undefined ? normalizeNumber(params.tax) : undefined,
        shipping: params.shipping !== undefined ? normalizeNumber(params.shipping) : undefined,
        transaction_id: params.transaction_id ? String(params.transaction_id) : undefined,
        payment_type: params.payment_type || undefined,
        shipping_tier: params.shipping_tier || undefined,
        coupon: params.coupon || undefined,
        item_list_id: params.item_list_id || undefined,
        item_list_name: params.item_list_name || undefined,
        promotion_id: params.promotion_id || undefined,
        promotion_name: params.promotion_name || undefined,
        creative_name: params.creative_name || undefined,
        creative_slot: params.creative_slot || undefined,
        items: isValidItems(params.items) ? params.items : undefined
    };

    return Object.fromEntries(Object.entries(eventParams).filter(([, v]) => v !== undefined));
}

/**
 * Sends a Google Analytics event.
 * @param {string} eventName 
 * @param {object} params 
 */
function sendGaEvent(eventName, params) {
    try {
        if (typeof window === "undefined") {
            return;
        }

        const gtagFn = typeof window.gtag === "function"
            ? window.gtag
            : (typeof globalThis !== "undefined" && typeof globalThis.gtag === "function" ? globalThis.gtag : null);

        if (gtagFn) {
            gtagFn("event", eventName, params);
            return;
        }

        if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: eventName, ...params });
        }
    } catch (e) {
        console.error(`GA Event [${eventName}] failed: `, e);
    }
}

let googleAnalyticsTagLoaded = false;

function getGoogleAnalyticsMeasurementId() {
    if (typeof document === "undefined") return null;
    return document.querySelector('meta[name="ga-measurement-id"]')?.content || null;
}

function syncGoogleAnalyticsWithConsent() {
    const measurementId = getGoogleAnalyticsMeasurementId();
    if (!measurementId || typeof window.gtag !== "function") return;

    const analyticsAllowed = isTrackingEnabled("analytics");
    window[`ga-disable-${measurementId}`] = !analyticsAllowed;
    if (!analyticsAllowed || googleAnalyticsTagLoaded) return;

    googleAnalyticsTagLoaded = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
    window.gtag("js", new Date());
    window.gtag("config", measurementId);
}

function initGoogleAnalytics() {
    if (typeof window === "undefined") return;
    syncGoogleAnalyticsWithConsent();
    window.addEventListener(CONSENT_CHANGED_EVENT, syncGoogleAnalyticsWithConsent);
}

const OPENAI_ADS_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const OPENAI_ADS_CONTENT_TYPE = "product";
const OPENAI_ADS_CHECKOUT_STARTED_PREFIX = "openAiAdsCheckoutStarted_";

let openAiAdsInitialized = false;
let openAiAdsConsentGranted = false;

function getOpenAiAdsPixelId() {
    if (typeof document === "undefined") return null;
    return document.querySelector('meta[name="openai-ads-pixel-id"]')?.content || null;
}

function toMinorUnits(value) {
    return Math.round(normalizeNumber(value) * 100);
}

function withoutUndefined(object) {
    return Object.fromEntries(Object.entries(object).filter(([, v]) => v !== undefined));
}

function buildOpenAiAdsContentsPayload(currency, value, items) {
    const currencyCode = currencyMapping(currency);
    return {
        type: "contents",
        amount: toMinorUnits(value),
        currency: currencyCode,
        contents: items.map(item => withoutUndefined({
            id: item.item_id,
            name: item.item_name,
            content_type: OPENAI_ADS_CONTENT_TYPE,
            quantity: normalizeNumber(item.quantity) || 1,
            amount: toMinorUnits(item.price),
            currency: item.currency ? currencyMapping(item.currency) : currencyCode
        }))
    };
}

function sendOpenAiAdsEvent(eventName, data, eventId) {
    if (!openAiAdsInitialized || !openAiAdsConsentGranted || !eventId) return false;
    try {
        window.oaiq("measure", eventName, data, { event_id: String(eventId) });
        return true;
    } catch (e) {
        console.error(`OpenAI Ads event [${eventName}] failed: `, e);
        return false;
    }
}

function rawTrackOpenAiAdsPageViewed() {
    sendOpenAiAdsEvent("page_viewed", {}, newFbEventId());
}
const trackOpenAiAdsPageViewed = withTrackingCheck(rawTrackOpenAiAdsPageViewed, "marketing");

function rawTrackOpenAiAdsContentsViewed() {
    const viewContent = typeof window !== "undefined" ? window.__MT_FB_VIEW_CONTENT__ : null;
    if (!viewContent || typeof viewContent !== "object" || !viewContent.event_id) return;
    if (!Array.isArray(viewContent.content_ids) || viewContent.content_ids.length === 0) return;

    sendOpenAiAdsEvent("contents_viewed", {
        type: "contents",
        contents: viewContent.content_ids.map(id => withoutUndefined({
            id: String(id),
            name: viewContent.content_name || undefined,
            content_type: OPENAI_ADS_CONTENT_TYPE,
            quantity: 1
        }))
    }, viewContent.event_id);
}
const trackOpenAiAdsContentsViewed = withTrackingCheck(rawTrackOpenAiAdsContentsViewed, "marketing");

function rawTrackOpenAiAdsItemsAdded(currency, value, items) {
    if (!isValidItems(items)) return;
    sendOpenAiAdsEvent("items_added", buildOpenAiAdsContentsPayload(currency, value, items), newFbEventId());
}
const trackOpenAiAdsItemsAdded = withTrackingCheck(rawTrackOpenAiAdsItemsAdded, "marketing");

function rawTrackOpenAiAdsCheckoutStarted(currency, value, items, cartId) {
    if (!isValidItems(items)) return;

    const storageKey = cartId ? `${OPENAI_ADS_CHECKOUT_STARTED_PREFIX}${cartId}` : null;
    try {
        if (storageKey && sessionStorage.getItem(storageKey) === "1") return;
    } catch (e) {}

    const sent = sendOpenAiAdsEvent("checkout_started", buildOpenAiAdsContentsPayload(currency, value, items), newFbEventId());
    if (!sent || !storageKey) return;
    try {
        sessionStorage.setItem(storageKey, "1");
    } catch (e) {}
}
const trackOpenAiAdsCheckoutStarted = withTrackingCheck(rawTrackOpenAiAdsCheckoutStarted, "marketing");

function trackOpenAiAdsOrderCreated(currency, value, items, orderNumber) {
    if (!orderNumber) return;
    sendOpenAiAdsEvent("order_created", buildOpenAiAdsContentsPayload(currency, value, items), orderNumber);
}

function syncOpenAiAdsWithConsent() {
    const pixelId = getOpenAiAdsPixelId();
    if (!pixelId) return;

    const marketingAllowed = isTrackingEnabled("marketing");
    if (!openAiAdsInitialized) {
        if (!marketingAllowed) return;

        openAiAdsInitialized = true;
        openAiAdsConsentGranted = true;
        window.oaiq = window.oaiq || function () {
            (window.oaiq.q = window.oaiq.q || []).push(arguments);
        };
        const script = document.createElement("script");
        script.async = true;
        script.src = OPENAI_ADS_SDK_URL;
        document.head.appendChild(script);
        window.oaiq("init", { pixelId: pixelId });
        window.oaiq("consent", true);
        trackOpenAiAdsPageViewed();
        trackOpenAiAdsContentsViewed();
        return;
    }

    if (marketingAllowed === openAiAdsConsentGranted) return;
    openAiAdsConsentGranted = marketingAllowed;
    try {
        window.oaiq("consent", marketingAllowed);
    } catch (e) {
        console.error("OpenAI Ads consent update failed: ", e);
    }
}

function initOpenAiAds() {
    if (typeof window === "undefined") return;
    syncOpenAiAdsWithConsent();
    window.addEventListener(CONSENT_CHANGED_EVENT, syncOpenAiAdsWithConsent);
}

/**
 * Tracks the view of an item list.
 * @param {string} itemListId
 * @param {string} itemListName
 * @param {Array} items
 */
function rawTrackViewItemList(itemListId, itemListName, items) {
    if (!isValidItems(items)) return console.warn("trackViewItemList: Invalid items");
    sendGaEvent(GA_EVENTS.VIEW_ITEM_LIST, buildEcommerceParams({
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: items
    }));
}
const trackViewItemList = withTrackingCheck(rawTrackViewItemList);

/**
 * Tracks item selection from a list.
 * @param {string} itemListId
 * @param {string} itemListName
 * @param {Array} items
 */
function rawTrackSelectItem(itemListId, itemListName, items) {
    if (!isValidItems(items)) return console.warn("trackSelectItem: Invalid items");
    sendGaEvent(GA_EVENTS.SELECT_ITEM, buildEcommerceParams({
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: items
    }));
}
const trackSelectItem = withTrackingCheck(rawTrackSelectItem);

/**
 * Tracks item detail views.
 * @param {string} currency
 * @param {number|string} value
 * @param {Array} items
 */
function rawTrackViewItem(currency, value, items) {
    if (!isValidItems(items)) return console.warn("trackViewItem: Invalid items");
    sendGaEvent(GA_EVENTS.VIEW_ITEM, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items
    }));
}
const trackViewItem = withTrackingCheck(rawTrackViewItem);

/**
 * Tracks the addition of an item to the cart.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackAddToCart(currency, value, items, coupon) {
    if (!isValidItems(items)) return console.warn("trackAddToCart: Invalid items");
    sendGaEvent(GA_EVENTS.ADD_TO_CART, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items,
        coupon: coupon
    }));
}
const trackGaAddToCart = withTrackingCheck(rawTrackAddToCart);

function trackAddToCart(currency, value, items, coupon) {
    trackGaAddToCart(currency, value, items, coupon);
    trackOpenAiAdsItemsAdded(currency, value, items);
}

/**
 * Tracks the removal of an item from the cart.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackRemoveFromCart(currency, value, items) {
    if (!isValidItems(items)) return console.warn("trackRemoveFromCart: Invalid items");
    sendGaEvent(GA_EVENTS.REMOVE_FROM_CART, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items
    }));
}
const trackRemoveFromCart = withTrackingCheck(rawTrackRemoveFromCart);

/**
 * Tracks the viewing of the cart.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackViewCart(currency, value, items) {
    if (!isValidItems(items)) return console.warn("trackViewCart: Invalid items");
    sendGaEvent(GA_EVENTS.VIEW_CART, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items
    }));
}
const trackViewCart = withTrackingCheck(rawTrackViewCart);

/**
 * Tracks the addition of shipping information.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackAddShippingInfo(currency, value, items, shippingTier, coupon) {
    if (!isValidItems(items)) return console.warn("trackAddShippingInfo: Invalid items");
    sendGaEvent(GA_EVENTS.ADD_SHIPPING_INFO, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items,
        shipping_tier: shippingTier,
        coupon: coupon
    }));
}
const trackAddShippingInfo = withTrackingCheck(rawTrackAddShippingInfo);

/**
 * Tracks the addition of payment information.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackAddPaymentInfo(payment_type, currency, value, items, coupon) {
    if (!isValidItems(items)) return console.warn("trackAddPaymentInfo: Invalid items");
    sendGaEvent(GA_EVENTS.ADD_PAYMENT_INFO, buildEcommerceParams({
        payment_type: payment_type || "unknown",
        currency: currency,
        value: value,
        items: items,
        coupon: coupon
    }));
}
const trackAddPaymentInfo = withTrackingCheck(rawTrackAddPaymentInfo);

/**
 * Tracks the beginning of the checkout process.
 * @param {string} currency 
 * @param {number} value 
 * @param {Array} items 
 * @returns 
 */
function rawTrackBeginCheckout(currency, value, items, coupon) {
    if (!isValidItems(items)) return console.warn("trackBeginCheckout: Invalid items");
    sendGaEvent(GA_EVENTS.BEGIN_CHECKOUT, buildEcommerceParams({
        currency: currency,
        value: value,
        items: items,
        coupon: coupon
    }));
}
const trackGaBeginCheckout = withTrackingCheck(rawTrackBeginCheckout);

function trackBeginCheckout(currency, value, items, coupon, cartId) {
    trackGaBeginCheckout(currency, value, items, coupon);
    trackOpenAiAdsCheckoutStarted(currency, value, items, cartId);
}

/**
 * Tracks usage of the Express-Checkout shortcut.
 * Custom GA4 event "express_checkout" — useful for funnel analysis and
 * comparing conversion rate vs. the regular multi-step flow.
 */
function rawTrackExpressCheckout(currency, value, items, coupon) {
    sendGaEvent("express_checkout", buildEcommerceParams({
        currency: currency,
        value: value,
        items: Array.isArray(items) ? items : [],
        coupon: coupon,
        dispatch_type: "print_at_home"
    }));
}
const trackExpressCheckout = withTrackingCheck(rawTrackExpressCheckout);

/**
 * Tracks usage of the Guest-Checkout shortcut.
 * Custom GA4 event "guest_checkout" — fired when a not-logged-in customer
 * chooses to continue without creating an account. Useful for measuring
 * adoption of guest checkout and comparing funnel completion vs. the
 * login-required flow.
 */
function rawTrackGuestCheckout(currency, value, items, coupon) {
    sendGaEvent("guest_checkout", buildEcommerceParams({
        currency: currency,
        value: value,
        items: Array.isArray(items) ? items : [],
        coupon: coupon
    }));
}
const trackGuestCheckout = withTrackingCheck(rawTrackGuestCheckout);

/**
 * Tracks the completion of a purchase.
 * All purchase trackers are gated on the `marketing` consent category via the
 * withTrackingCheck wrapper; the GA4 purchase event additionally requires `analytics`.
 * @param {string} currency
 * @param {number} value
 * @param {Array} items
 * @returns
 */
function rawTrackPurchase(currency, value, items, transaction_id, options = {}) {
    if (!isValidItems(items)) return console.warn("trackPurchase: Invalid items");
    if (!transaction_id) return console.warn("trackPurchase: Missing transaction_id");
    const numericValue = normalizeNumber(value);
    if (isTrackingEnabled("analytics")) {
        sendGaEvent(GA_EVENTS.PURCHASE, buildEcommerceParams({
            transaction_id: transaction_id,
            currency: currency,
            value: numericValue,
            tax: options.tax,
            shipping: options.shipping,
            coupon: options.coupon,
            items: items
        }));
    }

    try {
        window.uetq = window.uetq || [];
        window.uetq.push("event", "purchase", {
            "transaction_id": String(transaction_id),
            "ecomm_pagetype": "purchase",
            "ecomm_totalvalue": numericValue,
            "revenue_value": numericValue,
            "currency": currencyMapping(currency),
            "items": items,
        });
        window.uetq.push({ "gv": numericValue });
    } catch (e) {
        console.error("UET tracking failed:", e);
    }

    try {
        if (typeof window !== "undefined" && typeof window.Brevo !== "undefined") {
            const eventName = "order_completed";
            const contact = options.contact || {};
            const email = typeof contact.email === "string" ? contact.email.trim() : "";

            if (!email) {
                console.warn("Brevo tracking skipped: missing contact email");
            } else {
                const contactProperties = { email: email };
                if (contact.firstname) contactProperties.FIRSTNAME = String(contact.firstname);
                if (contact.lastname) contactProperties.LASTNAME = String(contact.lastname);

                const eventData = {
                    id: String(transaction_id),
                    data: {
                        total: numericValue,
                        currency: currencyMapping(currency),
                        items: items
                    }
                };

                if (Array.isArray(window.Brevo) && typeof window.Brevo.push === "function") {
                    window.Brevo.push(["identify", { identifiers: { email_id: email }, attributes: contactProperties }]);
                    window.Brevo.push(["track", eventName, contactProperties, eventData]);
                } else if (typeof window.Brevo.track === "function") {
                    if (typeof window.Brevo.identify === "function") {
                        window.Brevo.identify({ identifiers: { email_id: email }, attributes: contactProperties });
                    }
                    window.Brevo.track(eventName, contactProperties, eventData);
                }
            }
        }
    } catch (e) {
        console.error("Brevo tracking failed:", e);
    }

    trackOpenAiAdsOrderCreated(currency, numericValue, items, options.orderNumber);
}
const trackPurchase = withTrackingCheck(rawTrackPurchase, "marketing");

/**
 * Tracks refunds.
 * @param {string} currency
 * @param {number|string} value
 * @param {Array} items
 * @param {string|number} transaction_id
 */
function rawTrackRefund(currency, value, items, transaction_id) {
    if (!transaction_id) return console.warn("trackRefund: Missing transaction_id");

    sendGaEvent(GA_EVENTS.REFUND, buildEcommerceParams({
        transaction_id: transaction_id,
        currency: currency,
        value: value,
        items: items
    }));
}
const trackRefund = withTrackingCheck(rawTrackRefund);

/**
 * Tracks promotion views.
 * @param {string} promotionId
 * @param {string} promotionName
 * @param {string} creativeName
 * @param {string} creativeSlot
 * @param {Array} items
 */
function rawTrackViewPromotion(promotionId, promotionName, creativeName, creativeSlot, items) {
    sendGaEvent(GA_EVENTS.VIEW_PROMOTION, buildEcommerceParams({
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: creativeName,
        creative_slot: creativeSlot,
        items: items
    }));
}
const trackViewPromotion = withTrackingCheck(rawTrackViewPromotion);

/**
 * Tracks promotion selection.
 * @param {string} promotionId
 * @param {string} promotionName
 * @param {string} creativeName
 * @param {string} creativeSlot
 * @param {Array} items
 */
function rawTrackSelectPromotion(promotionId, promotionName, creativeName, creativeSlot, items) {
    sendGaEvent(GA_EVENTS.SELECT_PROMOTION, buildEcommerceParams({
        promotion_id: promotionId,
        promotion_name: promotionName,
        creative_name: creativeName,
        creative_slot: creativeSlot,
        items: items
    }));
}
const trackSelectPromotion = withTrackingCheck(rawTrackSelectPromotion);

/**
 * Tracks the login event.
 */
function rawTrackLogin() {
    sendGaEvent(GA_EVENTS.LOGIN, { method: GA_METHODS.MT });
}
const trackLogin = withTrackingCheck(rawTrackLogin);

/**
 * Tracks the registration event.
 */
function rawTrackRegister() {
    sendGaEvent(GA_EVENTS.SIGN_UP, { method: GA_METHODS.MT });
}
const trackRegister = withTrackingCheck(rawTrackRegister);

/**
 * Tracks the sharing of a link.
 * @param {string} shareMethod 
 */
function rawShareLink(shareMethod) {
    sendGaEvent(GA_EVENTS.SHARE, { 
        method: shareMethod || "unknown",
        content_type: "link"
    });
}
const shareLink = withTrackingCheck(rawShareLink);

/**
 * Tracks the selection of content.
 * @param {string} contentType 
 * @param {string} contentId 
 */
function rawSelectContent(contentType, contentId) {
    sendGaEvent(GA_EVENTS.SELECT_CONTENT, {
        content_type: contentType || "unknown",
        content_id: contentId || "unknown"
    });
}
const selectContent = withTrackingCheck(rawSelectContent);

/**
 * Tracks a search event.
 * @param {string} query
 */
function rawSearch(query) {
    sendGaEvent(GA_EVENTS.SEARCH, {
        search_term: query || "unknown"
    });
}
const search = withTrackingCheck(rawSearch);

/**
 * Tracks the rendering of a search results list.
 * Custom GA4 event "view_search_results" — complements `search` to measure
 * how often a query yields zero results (drop-off signal) and which
 * queries convert to clicks/purchases.
 * @param {string} query
 * @param {number} resultsCount
 * @param {Array} items optional GA4 items array of the visible results
 */
function rawTrackViewSearchResults(query, resultsCount, items) {
    sendGaEvent("view_search_results", {
        search_term: query || "unknown",
        results_count: normalizeNumber(resultsCount),
        items: isValidItems(items) ? items : undefined
    });
}
const trackViewSearchResults = withTrackingCheck(rawTrackViewSearchResults);

/**
 * Tracks successful redemption of a voucher / discount code in the cart.
 * Custom GA4 event "voucher_redeem" — used for campaign ROI and
 * promotion-code performance reports.
 * @param {string} code
 * @param {string} currency
 * @param {number|string} value discount value applied
 * @param {string} kind "voucher" | "discount"
 */
function rawTrackVoucherRedeem(code, currency, value, kind) {
    sendGaEvent("voucher_redeem", {
        coupon: code || "unknown",
        currency: currency !== undefined ? currencyMapping(currency) : undefined,
        value: value !== undefined ? normalizeNumber(value) : undefined,
        redeem_type: kind || "voucher"
    });
}
const trackVoucherRedeem = withTrackingCheck(rawTrackVoucherRedeem);

/**
 * Tracks a waitlist signup. Custom GA4 event "waitlist_signup" — micro-
 * conversion useful for sold-out events.
 * @param {string|number} dateId
 * @param {string} eventName
 */
function rawTrackWaitlistSignup(dateId, eventName) {
    sendGaEvent("waitlist_signup", {
        date_id: dateId ? String(dateId) : undefined,
        event_name: eventName || undefined
    });
}
const trackWaitlistSignup = withTrackingCheck(rawTrackWaitlistSignup);

/**
 * Tracks any error that interrupts a checkout step (validation, network,
 * cart-merge failures, etc.). Custom GA4 event "checkout_step_error".
 * @param {string} step e.g. "shipping", "payment", "review"
 * @param {string} message short, non-PII description
 * @param {string} code optional machine-readable error code
 */
function rawTrackCheckoutStepError(step, message, code) {
    sendGaEvent("checkout_step_error", {
        step: step || "unknown",
        error_message: message ? String(message).slice(0, 250) : undefined,
        error_code: code || undefined
    });
}
const trackCheckoutStepError = withTrackingCheck(rawTrackCheckoutStepError);

/**
 * Tracks a failed payment attempt. Custom GA4 event "payment_failed" —
 * separate from `checkout_step_error` so payment-provider drop-offs
 * (Stripe declines, SEPA validation, PayPal cancel) are isolated.
 * @param {string} paymentType
 * @param {string} currency
 * @param {number|string} value
 * @param {string} message
 * @param {string} code
 */
function rawTrackPaymentFailed(paymentType, currency, value, message, code) {
    sendGaEvent("payment_failed", {
        payment_type: paymentType || "unknown",
        currency: currency !== undefined ? currencyMapping(currency) : undefined,
        value: value !== undefined ? normalizeNumber(value) : undefined,
        error_message: message ? String(message).slice(0, 250) : undefined,
        error_code: code || undefined
    });
}
const trackPaymentFailed = withTrackingCheck(rawTrackPaymentFailed);

/**
 * Tracks a ticket download by a logged-in customer. Custom GA4 event
 * "ticket_download" — indicator of active customers and helps correlate
 * post-purchase engagement with later repeat-purchase behavior.
 * @param {string|number} orderId
 * @param {string|number} ticketId optional, when known
 * @param {string} format "pdf" | "wallet"
 */
function rawTrackTicketDownload(orderId, ticketId, format) {
    sendGaEvent("ticket_download", {
        transaction_id: orderId ? String(orderId) : undefined,
        ticket_id: ticketId ? String(ticketId) : undefined,
        format: format || "pdf"
    });
}
const trackTicketDownload = withTrackingCheck(rawTrackTicketDownload);

/**
 * Generates an event_id that is shared between the first-party Facebook browser
 * pixel and the server-side Conversion API (CAPI) call, so Meta can deduplicate
 * the two. Falls back to a manual UUID when crypto.randomUUID is unavailable.
 * @returns {string}
 */
function newFbEventId() {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch (e) {
        // ignore and fall back
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Fires a Facebook browser-pixel event that mirrors a CAPI event using the same
 * `eventID`, enabling Meta server+browser deduplication. The base pixel
 * (`fbq('init')` / fbevents.js) is loaded per partner via app-config
 * (HEAD_INSERTS) — this only tracks, it never initializes a pixel. No-op when no
 * pixel is present. Gated on `marketing` consent (like UET/Brevo).
 * @param {string} eventName e.g. "AddToCart" | "ViewContent"
 * @param {string} eventId shared event_id (see newFbEventId)
 * @param {object} customData Meta custom_data (value, currency, content_ids, …)
 */
function rawFireFbBrowserEvent(eventName, eventId, customData = {}) {
    try {
        if (typeof window === "undefined" || typeof window.fbq !== "function") {
            return;
        }
        if (!eventName || !eventId) {
            return;
        }
        window.fbq("track", eventName, customData || {}, { eventID: String(eventId) });
    } catch (e) {
        console.error(`FB pixel event [${eventName}] failed: `, e);
    }
}
const fireFbBrowserEvent = withTrackingCheck(rawFireFbBrowserEvent, "marketing");

/**
 * Mirrors a server-rendered ViewContent CAPI event into the browser pixel.
 * Reads the descriptor the backend exposed via `window.__MT_FB_VIEW_CONTENT__`
 * (set in the page blade) and fires it once. Safe to call on every page load —
 * a no-op when the global is absent.
 */
function fireFbViewContentFromPage() {
    try {
        if (typeof window === "undefined") {
            return;
        }
        const vc = window.__MT_FB_VIEW_CONTENT__;
        if (!vc || typeof vc !== "object" || !vc.event_id) {
            return;
        }
        fireFbBrowserEvent("ViewContent", vc.event_id, {
            content_type: vc.content_type || "product",
            content_ids: Array.isArray(vc.content_ids) ? vc.content_ids : undefined,
            content_name: vc.content_name || undefined,
        });
    } catch (e) {
        // never break page bootstrap over tracking
    }
}

export {
    trackViewItemList,
    trackSelectItem,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackBeginCheckout,
    trackExpressCheckout,
    trackGuestCheckout,
    trackPurchase,
    trackRefund,
    trackViewPromotion,
    trackSelectPromotion,
    trackLogin,
    trackRegister,
    shareLink,
    selectContent,
    search,
    trackViewSearchResults,
    trackVoucherRedeem,
    trackWaitlistSignup,
    trackCheckoutStepError,
    trackPaymentFailed,
    trackTicketDownload,
    getCartItemsForGA,
    newFbEventId,
    fireFbBrowserEvent,
    fireFbViewContentFromPage,
    initGoogleAnalytics,
    initOpenAiAds,
}
